import { useScreenSize } from '@hooks/useScreenSize';
import { Search } from '@mui/icons-material';
import { Box, Divider, List, ListItem, ListItemButton, ListItemText, TextField, Tooltip, Typography } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface SearchItem {
  id: string;
  name: string;
  secondaryName?: string;
}

type SearchLookupProps = {
  items: SearchItem[];
  expanded: boolean;
  maxListHeight?: string;
  onClose: () => void;
  tooltip?: string;
};

const debounce = (func: (...args: any[]) => void, delay: number) => {
  let timeoutId: NodeJS.Timeout;
  return (...args: any[]) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

const KEYBOARD_THROTTLE = 200;

const ProductLookup: React.FC<SearchLookupProps> = ({ items, expanded, maxListHeight, onClose, tooltip }) => {
  const [searchValue, setSearchValue] = useState('');
  const [filteredItems, setFilteredItems] = useState<SearchItem[]>(items);
  const [hoveringList, setHoveringList] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const itemRefs = useRef<HTMLDivElement[]>([]);
  const lastKeyTimeRef = useRef(0);
  const lastKeyRef = useRef<string | null>(null);
  const navigate = useNavigate();
  const { isMobile, isMobileLandscape } = useScreenSize();

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') handleSearchBoxClose(true);
    };

    if (expanded) document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      setSearchValue('');
      setFilteredItems(items);
    };
  }, [expanded]);

  const performFiltering = useCallback(
    (value: string) => {
      const filtered = items.filter(
        item => item.name.toLowerCase().includes(value.toLowerCase()) || item.secondaryName?.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredItems(filtered);
    },
    [items]
  );

  const debouncedFilter = useCallback(debounce(performFiltering, 300), [performFiltering]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchValue(value);
    debouncedFilter(value);
    setHighlightedIndex(-1);
  };

  const handleKeyboardPress = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const now = Date.now();
    const isHoldingSameKey = event.key === lastKeyRef.current;

    if (isHoldingSameKey && now - lastKeyTimeRef.current < KEYBOARD_THROTTLE) return;
    lastKeyTimeRef.current = now;
    lastKeyRef.current = event.key;

    if (!filteredItems.length) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlightedIndex(prev => (prev + 1) % filteredItems.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlightedIndex(prev => (prev === -1 ? filteredItems.length - 1 : (prev - 1 + filteredItems.length) % filteredItems.length));
    } else if (event.key === 'Enter') {
      if (highlightedIndex >= 0) {
        handleProductClick(filteredItems[highlightedIndex]);
      }
    }
  };

  const handleSearchBoxClose = (forceBlur?: boolean) => {
    if (forceBlur || !hoveringList) {
      setSearchValue('');
      setFilteredItems(items);
      onClose();
    }
  };

  const handleProductClick = (product: SearchItem) => {
    navigate(`/reference/products/${product.secondaryName}`);
    setHoveringList(false);
    handleSearchBoxClose(true);
  };

  useEffect(() => {
    setFilteredItems(items);
  }, [items]);

  useEffect(() => {
    if (highlightedIndex >= 0 && itemRefs.current[highlightedIndex]) {
      itemRefs.current[highlightedIndex].scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [highlightedIndex]);

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          padding: '4px 8px',
          borderRadius: '20px',
          cursor: 'pointer',
          flexGrow: 1,
          backgroundColor: expanded ? '#757575' : 'transparent',
          transition: 'all 0.3s ease-in-out',
          width: isMobile || isMobileLandscape ? 'auto' : expanded ? '270px' : '30px',
          overflow: 'hidden',
        }}
      >
        <TextField
          value={searchValue}
          onChange={handleSearchChange}
          placeholder="Product Lookup"
          variant="standard"
          size="small"
          fullWidth
          autoFocus
          slotProps={{
            input: {
              disableUnderline: true,
              style: { fontSize: 11 },
            },
            htmlInput: {
              autoComplete: 'off',
            },
          }}
          sx={{
            marginLeft: '5px',
            flexGrow: 1,
            fontSize: 11,
            overflow: 'hidden',
            opacity: expanded ? 1 : 0,
            transition: 'opacity 0.3s ease-in-out',
          }}
          onKeyDown={handleKeyboardPress}
        />
        <Tooltip title={tooltip}>
          <Search sx={{ fontSize: 15, marginRight: '5px', marginLeft: '0' }} />
        </Tooltip>
      </Box>
      {expanded && searchValue && (
        <Box
          sx={{
            position: 'absolute',
            top: '40px',
            right: 0,
            width: '100%',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
            borderRadius: '4px',
            maxHeight: maxListHeight,
            overflowY: 'auto',
            zIndex: 100,
            fontSize: 11,
            backgroundColor: '#757575',
          }}
          onMouseEnter={() => setHoveringList(true)}
          onMouseLeave={() => setHoveringList(false)}
        >
          <List>
            {filteredItems.length > 0 ? (
              filteredItems.map((item, index) => (
                <>
                  <ListItemButton
                    key={item.id}
                    ref={el => (itemRefs.current[index] = el!)}
                    data-closable
                    selected={index === highlightedIndex}
                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                    onClick={() => handleProductClick(item)}
                  >
                    <Typography data-closable variant="body1" style={{ fontSize: 10 }}>
                      {item.name}
                    </Typography>
                    <Typography data-closable variant="body2" style={{ fontSize: 10 }}>
                      {item.secondaryName}
                    </Typography>
                  </ListItemButton>
                  <Divider />
                </>
              ))
            ) : (
              <ListItem>
                <ListItemText primaryTypographyProps={{ fontSize: 11 }} primary="No results found" />
              </ListItem>
            )}
          </List>
        </Box>
      )}
    </>
  );
};

export default ProductLookup;
